import React, { FC, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import { Collapse, Menu, MenuItem, useMediaQuery, useTheme } from "@mui/material";

import { openConsentPreferences } from "@utils/consent";
import { ERoutes } from "@utils/constants";
import styled from "styled-components";

import { ShopLogoIcon } from "@/assets/svg";
import {
  FacebookLogo,
  GoldenAppleMusicLogo,
  GoldenBandcampLogo,
  GoldenSoundCloudLogo,
  GoldenSpotifyLogo,
  GoldenYouTubeLogo,
  InstagramLogo,
  TwitterLogo,
} from "@/assets/svg/Links";
import { appleMusic, bandcamp, soundcloud, spotify, youtube } from "@/components/IconButtonLinks/constants";
import { FooterIconMenu } from "@/components/IconButtonLinks/Menu";
import Dialog from "@/pages/HomePage/components/JoinOurNewsletter/components/Dialog";

import Form from "./Form";

const FN = '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';

/* ── columns (same links as the structure demo) ── */
type TLink = { label: string; to: string; cookies?: boolean; emphasis?: boolean };
const COLUMNS: { title: string; links: TLink[] }[] = [
  {
    title: "Browse",
    links: [
      { label: "Music", to: ERoutes.Catalog },
      { label: "Pricing", to: ERoutes.Pricing },
      { label: "Deals", to: ERoutes.Deals },
    ],
  },
  {
    title: "Creators",
    links: [
      { label: "Features", to: ERoutes.HowItWorks },
      { label: "YouTube Use", to: ERoutes.FreeUseTerms },
      { label: "How to Download", to: ERoutes.DownloadInstruction },
    ],
  },
  {
    title: "Licensing",
    links: [
      { label: "How to License", to: ERoutes.CatalogLandingLicensing },
      { label: "License request", to: ERoutes.Licensing },
      { label: "Music & Ambient Creators", to: ERoutes.MUSIC_CREATOR_PROGRAM },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "Contact", to: ERoutes.Contact },
      { label: "Submit Music", to: ERoutes.Submit },
      // Replaced the Newsletter link. The newsletter already has the signup
      // band directly above this strip, so the link sent someone to a page for
      // the thing they were already looking at.
      { label: "Updates", to: ERoutes.WhatsNew },
    ],
  },
];

/* The withdrawal link is the statutory "withdrawal button" (§ 13a FAGG), not an
   ordinary legal link: it must be permanently available, prominent and legible
   throughout the customer journey, and its label has to be the statutory phrase
   or an unambiguous equivalent. Do not shorten it to "Withdraw" to save space in
   the strip, and do not tuck it inside the Cookies-style dropdown — both would
   cost it the prominence the rule requires. */
const LEGAL: TLink[] = [
  { label: "Privacy", to: ERoutes.Privacy },
  { label: "Imprint", to: ERoutes.Imprint },
  { label: "Terms", to: ERoutes.Conditions },
  { label: "YouTube License", to: ERoutes.LicenceTerms },
  { label: "Refund", to: ERoutes.RefundPolicy },
  { label: "Cookies", to: "", cookies: true },
  { label: "Withdraw from contract", to: ERoutes.Withdraw, emphasis: true },
];

/* Footer "Cookies" dropdown — mirrors the socials FooterIconMenu look, groups
   the two cookie affordances (settings window + policy page) behind one entry
   so the compact mobile legal strip stays a single tidy line. */
const cookieMenuPaperSx = {
  mt: "-8px",
  minWidth: "180px",
  overflow: "visible",
  border: "1px solid rgba(235,194,142,0.18)",
  borderRadius: "12px",
  backgroundColor: "transparent",
  backgroundImage: "linear-gradient(180deg, rgb(153, 112, 75) 0%, rgb(111, 83, 60) 100%)",
  filter: "drop-shadow(0px 14px 44px rgba(0,0,0,0.4))",
} as const;

const cookieItemSx = {
  fontFamily: "Raleway",
  fontSize: "13px",
  color: "#e0d2c1",
  "&:hover": { background: "rgba(255,255,255,0.06)", color: "#fff" },
} as const;

/* ── styled ── */
const FooterWrapper = styled.footer`
  width: 100%;
  position: relative;
  color: #fbf4ec;
  background:
    radial-gradient(90% 70% at 50% 0%, rgba(255, 222, 170, 0.07), transparent 62%),
    #2e1d12;
  border-top: 1px solid rgba(235, 194, 142, 0.08);
`;

const Wrap = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 16px;
  @media (min-width: 900px) {
    padding: 0 40px;
  }
`;

/* newsletter band */
const NewsletterBand = styled.div`
  border-bottom: 1px solid rgba(235, 194, 142, 0.08);
  /* phone: drop the full-width divider below the newsletter */
  @media (max-width: 599px) {
    border-bottom: none;
  }
`;
const NewsletterInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px 32px;
  flex-wrap: wrap;
  padding: 30px 0;
  /* desktop: heading left, form right. below 900: heading on top, form full-width
     below (so the inputs get the whole band width on tablet). */
  @media (max-width: 899px) {
    flex-direction: column;
    align-items: stretch;
    gap: 18px;
  }
`;
const NLHeading = styled.h4`
  font-family: "Cormorant Garamond", serif;
  font-weight: 500;
  font-size: 1.5rem;
  color: #fff;
  margin: 0;
`;
const NLSub = styled.p`
  font-family: "Raleway", sans-serif;
  font-size: 0.84rem;
  color: rgba(251, 244, 236, 0.55);
  margin: 0;
`;
/* left text block — only constrained on desktop where it sits beside the form */
const NLText = styled.div`
  @media (min-width: 900px) {
    flex-shrink: 0;
    max-width: 340px;
  }
`;
/* dark input styling for the reused Form; the golden GradientButton is left as-is */
const FormDark = styled.div`
  width: 100%;
  /* desktop: fill the width next to the heading. below 900 the band stacks, so
     the form is already full-width. */
  @media (min-width: 900px) {
    flex: 1;
  }
  & .MuiInputBase-root {
    background: rgba(255, 255, 255, 0.05) !important;
    border: 1px solid rgba(235, 194, 142, 0.18) !important;
    border-radius: 10px !important;
    color: #fbf4ec !important;
    font-family: "Raleway" !important;
    font-size: 0.9rem !important;
    height: 46px !important;
  }
  & .MuiInputBase-input {
    color: #fbf4ec !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
  & .MuiInputBase-input::placeholder {
    color: rgba(251, 244, 236, 0.4) !important;
    opacity: 1 !important;
  }
  & .MuiOutlinedInput-notchedOutline {
    border: none !important;
  }
  & .MuiInputLabel-root {
    display: none !important;
  }
  /* match the home "Browse music" button font (Raleway, weight 500, +letter-spacing) */
  & .MuiButton-root {
    font-weight: 500 !important;
    letter-spacing: 0.3px !important;
  }
  /* in the horizontal row, make the button exactly as tall as the input boxes */
  @media (min-width: 640px) {
    & .MuiButton-root {
      height: 100% !important;
    }
  }
`;

/* link columns */
const Cols = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 34px;
  padding: 44px 0 4px;
`;
const ColTitle = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 0.72rem;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #ffdca8;
  margin-bottom: 14px;
`;
const ColLink = styled(RouterLink)`
  display: block;
  font-family: "Raleway", sans-serif;
  font-size: 0.86rem;
  color: #e0d2c1;
  padding: 5px 0;
  text-decoration: none;
  transition: color 0.2s ease;
  /* Blue on hover, matching the link hover used across the site. White read as
     "slightly brighter text" rather than as a link responding. */
  &:hover {
    color: #9cd3fd;
  }
`;

/* phone accordions */
const Accordions = styled.div`
  padding: 8px 0 4px;
`;
const AccItem = styled.div`
  border-top: 1px solid rgba(235, 194, 142, 0.08);
`;
const AccSummary = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: none;
  border: none;
  cursor: pointer;
  padding: 14px 2px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 0.74rem;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #ffdca8;
  svg {
    width: 14px;
    height: 14px;
    stroke: rgba(251, 244, 236, 0.55);
    fill: none;
    stroke-width: 2;
    transition: transform 0.2s ease;
  }
  &[data-open="true"] svg {
    transform: rotate(180deg);
  }
`;
const AccLinks = styled.div`
  padding: 2px 2px 12px;
`;

/* ── social section (demo-faithful: flex + display:contents, no MUI grid) ──
   desktop ≥900: 3 labelled groups in a row
   tablet 600–899: everything flattened into ONE centred row, headings hidden
   phone <900-below-600: groups stack with their headings */
const Social = styled.div`
  margin-top: 30px;
  padding-top: 28px;
  padding-bottom: 40px;
  border-top: 1px solid rgba(235, 194, 142, 0.08);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 54px;
  @media (max-width: 899px) {
    gap: 16px;
    align-items: center;
  }
  @media (max-width: 599px) {
    flex-direction: column;
    margin-top: 0;
    gap: 26px;
  }
`;
const Grp = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  /* tablet: dissolve the group so its icons flow into the single row */
  @media (min-width: 600px) and (max-width: 899px) {
    display: contents;
  }
`;
const H6 = styled.h6`
  font-family: ${FN};
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #ffdca8;
  margin: 0;
  /* hide the Socials / Streaming / Shop labels on tablet AND phone */
  @media (max-width: 899px) {
    display: none;
  }
`;
const Icons = styled.div`
  display: flex;
  gap: 13px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  /* tablet: dissolve so individual icons sit in the row */
  @media (min-width: 600px) and (max-width: 899px) {
    display: contents;
  }
`;
const Sico = styled.a`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  svg {
    width: 22px;
    height: 22px;
  }
  svg path {
    transition: fill 0.2s ease;
  }
  &:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(235, 194, 142, 0.3);
    svg path {
      fill: #b0dfff;
    }
  }
`;
/* glass-circle trigger around the streaming dropdown menus */
const GlassTrigger = styled.div`
  display: inline-flex;
  .MuiIconButton-root {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.1);
    transition: all 0.2s ease;
    &::before,
    &::after {
      display: none;
    }
    svg {
      width: 22px;
      height: 22px;
    }
    path {
      transition: fill 0.2s ease;
    }
    &:hover {
      background: rgba(255, 255, 255, 0.12);
      border-color: rgba(235, 194, 142, 0.3);
      path {
        fill: #b0dfff;
      }
    }
  }
`;
const ShopBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 11px 22px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #ffdca8;
  font-family: "Raleway", sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  svg {
    width: 20px;
    height: 15px;
  }
  svg path {
    fill: #ffdca8;
    transition: fill 0.2s ease;
  }
  &:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(235, 194, 142, 0.3);
    /* icon only turns blue on hover (like the socials); the label stays gold */
    svg path {
      fill: #b0dfff;
    }
  }
`;

/* bottom legal band */
const BottomBand = styled.div`
  background: rgba(255, 210, 155, 0.05);
  border-top: 1px solid rgba(235, 194, 142, 0.08);
`;
const BottomInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px 18px;
  padding: 18px 0;
  font-family: "Raleway", sans-serif;
  font-size: 0.78rem;
  color: rgba(251, 244, 236, 0.55);
  /* phone: the legal strip now carries five links — too many for one narrow
     line — so stack into two centered rows: copyright on top, links below. */
  @media (max-width: 599px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 12px;
    font-size: 0.7rem;
  }
`;
const RMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  flex: none;
  border-radius: 50%;
  border: 1px solid currentColor;
  font-size: 8px;
  font-weight: 600;
  line-height: 1;
  margin-right: 5px;
  /* A circle has no baseline worth aligning to, so it opts out of the parent's
     baseline alignment and centres on the line instead. Replaces a
     vertical-align:middle (a no-op on a flex item — flex ignores it) plus a
     translateY(-1px) nudge that was only compensating for that dead rule.
     NB: no backticks in this comment — they terminate the styled-components
     template literal. */
  align-self: center;

  /* phone: the ® circle optically sits a touch low against the year/text — lift
     it 2px so it reads level on mobile. */
  @media (max-width: 599px) {
    transform: translateY(-2px);
  }
`;
const Years = styled.span`
  font-family: ${FN};
`;
/* Copyright line — inline-flex so the ® mark, the year and "Audio Sanctum" sit
   on one line. Only the YEAR uses the system number font; the rest stays Raleway
   so it matches the legal links' size exactly.

   BASELINE, not center: the year and "Audio Sanctum" are in different font
   families, and centring aligns their BOXES, not their glyphs — each font seats
   its text at a different height inside the same box, so the two ended up
   visibly off. Baseline alignment is the thing that makes mixed fonts sit level.
   The ® circle opts out via align-self (see RMark). */
const Copyright = styled.span`
  display: inline-flex;
  align-items: baseline;
  line-height: 1;
`;
const LegalRow = styled.div`
  display: flex;
  align-items: center;
  line-height: 1;
  gap: 18px;
  flex-wrap: wrap;
  /* phone: centered under the copyright, wrapping if the links ever overflow */
  @media (max-width: 599px) {
    justify-content: center;
    gap: 8px 14px;
  }
  a {
    color: rgba(251, 244, 236, 0.55);
    text-decoration: none;
    cursor: pointer;
    &:hover {
      color: #fff;
    }
  }

  /* The withdrawal button must be legible and prominent, not one more muted
     link in a row of five (§ 13a FAGG). Full-strength text plus a hairline
     border lifts it out of the strip without turning the footer into a CTA. */
  a[data-emphasis="true"] {
    color: #ffdca8;
    padding: 5px 12px;
    border: 1px solid rgba(235, 194, 142, 0.35);
    border-radius: 14px;
    &:hover {
      color: #fff;
      border-color: rgba(235, 194, 142, 0.6);
      background: rgba(255, 255, 255, 0.05);
    }
    /* phone: the LegalBreak already puts two 8px row-gaps (16px) above the
       button; trim 4px so the space matches the ~12px gap between the © line and
       the menu items above it. */
    @media (max-width: 599px) {
      margin-top: -4px;
    }
  }
`;

/* A zero-height, full-width flex break rendered right before the withdrawal
   button on phones, so the statutory button always lands on its own line rather
   than sharing a wrapped row with "Cookies". */
const LegalBreak = styled.span`
  display: none;
  @media (max-width: 599px) {
    display: block;
    /* flex: 0 0 100% — must NOT shrink, or the flex algorithm collapses it and
       the line break never happens (leaving the button inline). */
    flex: 0 0 100%;
    width: 100%;
    height: 0;
  }
`;

const Chevron = () => (
  <svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9" /></svg>
);

interface ISecondFooter {
  isNewsletter?: boolean;
}

const SecondFooter: FC<ISecondFooter> = ({ isNewsletter = true }) => {
  const theme = useTheme();
  const isPhone = !useMediaQuery(theme.breakpoints.up("sm")); // < 600px → accordions
  const [openDialog, setOpenDialog] = useState(false);
  const [openCols, setOpenCols] = useState<Record<number, boolean>>({});
  const [cookieAnchor, setCookieAnchor] = useState<null | HTMLElement>(null);
  const [pendingCookieNav, setPendingCookieNav] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleCookieClock = () => {
    openConsentPreferences();
  };

  const renderColLink = (l: TLink) => (
    <ColLink key={l.label} to={l.to}>
      {l.label}
    </ColLink>
  );

  return (
    <FooterWrapper data-footer>
      {isNewsletter && (
        <NewsletterBand>
          <Wrap>
            <NewsletterInner>
              <NLText>
                <NLHeading>Newsletter</NLHeading>
                <NLSub>Releases, products and offers to your inbox.</NLSub>
              </NLText>
              <FormDark>
                <Form handleOpenDialog={() => setOpenDialog(true)} />
              </FormDark>
            </NewsletterInner>
          </Wrap>
        </NewsletterBand>
      )}

      <Wrap>
        {isPhone ? (
          <Accordions>
            {COLUMNS.map((col, i) => {
              const open = !!openCols[i];
              return (
                <AccItem
                  key={col.title}
                  // When the newsletter band is hidden there's nothing above the
                  // first column, so its top border would sit right under the
                  // footer's own top rule as a stray double line — drop it.
                  style={i === 0 && !isNewsletter ? { borderTop: "none" } : undefined}
                >
                  <AccSummary
                    data-open={open}
                    onClick={() => setOpenCols((s) => ({ ...s, [i]: !s[i] }))}
                  >
                    {col.title}
                    <Chevron />
                  </AccSummary>
                  <Collapse in={open} timeout={240}>
                    <AccLinks>{col.links.map((l) => renderColLink(l))}</AccLinks>
                  </Collapse>
                </AccItem>
              );
            })}
          </Accordions>
        ) : (
          <Cols>
            {COLUMNS.map((col) => (
              <div key={col.title}>
                <ColTitle>{col.title}</ColTitle>
                {col.links.map((l) => renderColLink(l))}
              </div>
            ))}
          </Cols>
        )}

        <Social>
          <Grp>
            <H6>Socials</H6>
            <Icons>
              <Sico href={ERoutes.FaceBook} target="_blank" rel="noreferrer"><FacebookLogo /></Sico>
              <Sico href={ERoutes.Instagram} target="_blank" rel="noreferrer"><InstagramLogo /></Sico>
              <Sico href={ERoutes.Twitter} target="_blank" rel="noreferrer"><TwitterLogo /></Sico>
            </Icons>
          </Grp>
          <Grp>
            <H6>Streaming</H6>
            <Icons>
              <GlassTrigger>
                <FooterIconMenu title={youtube.title} links={youtube.links}><GoldenYouTubeLogo /></FooterIconMenu>
              </GlassTrigger>
              <GlassTrigger>
                <FooterIconMenu title={spotify.title} links={spotify.links}><GoldenSpotifyLogo /></FooterIconMenu>
              </GlassTrigger>
              <GlassTrigger>
                <FooterIconMenu title={soundcloud.title} links={soundcloud.links}><GoldenSoundCloudLogo /></FooterIconMenu>
              </GlassTrigger>
              <Sico href={bandcamp} target="_blank" rel="noreferrer"><GoldenBandcampLogo /></Sico>
              <Sico href={appleMusic} target="_blank" rel="noreferrer"><GoldenAppleMusicLogo /></Sico>
            </Icons>
          </Grp>
          <Grp>
            <H6>Shop</H6>
            <ShopBtn onClick={() => window.open(ERoutes.Shop, "_blank")}>
              <ShopLogoIcon width={20} height={15} />
              Visit shop
            </ShopBtn>
          </Grp>
        </Social>
      </Wrap>

      <BottomBand>
        <Wrap>
          <BottomInner>
            <Copyright>
              <RMark>R</RMark>
              <Years>2024–{new Date().getFullYear()}</Years>
              &nbsp;Audio Sanctum is a registered trademark.
            </Copyright>
            <LegalRow>
              {LEGAL.map((l) => {
                if (l.cookies) {
                  return (
                    <a
                      key={l.label}
                      onClick={(e) => {
                        e.preventDefault();
                        setCookieAnchor(cookieAnchor ? null : e.currentTarget);
                      }}
                    >
                      {l.label}
                    </a>
                  );
                }
                const link = (
                  <RouterLink to={l.to} data-emphasis={l.emphasis ? "true" : undefined}>
                    {l.label}
                  </RouterLink>
                );
                // The withdrawal button gets a preceding full-width break so it
                // always lands on its own line on mobile, clearly separated.
                return l.emphasis ? (
                  <React.Fragment key={l.label}>
                    <LegalBreak />
                    {link}
                  </React.Fragment>
                ) : (
                  <React.Fragment key={l.label}>{link}</React.Fragment>
                );
              })}
            </LegalRow>
            <Menu
              anchorEl={cookieAnchor}
              open={Boolean(cookieAnchor)}
              onClose={() => setCookieAnchor(null)}
              anchorOrigin={{ vertical: "top", horizontal: "left" }}
              transformOrigin={{ vertical: "bottom", horizontal: "left" }}
              PaperProps={{ sx: cookieMenuPaperSx }}
              MenuListProps={{ sx: { py: "6px" } }}
              transitionDuration={140}
              // Navigate only AFTER the menu has fully closed and its portal +
              // click-catching backdrop have unmounted. Navigating synchronously
              // from the item swapped the page mid-close, which could orphan the
              // overlay (stuck top-left, blocking the whole page).
              TransitionProps={{
                onExited: () => {
                  if (pendingCookieNav) {
                    const to = pendingCookieNav;
                    setPendingCookieNav(null);
                    navigate(to);
                  }
                },
              }}
            >
              <MenuItem
                sx={cookieItemSx}
                onClick={() => {
                  setCookieAnchor(null);
                  handleCookieClock();
                }}
              >
                Cookie settings
              </MenuItem>
              <MenuItem
                sx={cookieItemSx}
                onClick={() => {
                  setPendingCookieNav(ERoutes.CookiePolicy);
                  setCookieAnchor(null);
                }}
              >
                Cookie policy
              </MenuItem>
            </Menu>
          </BottomInner>
        </Wrap>
      </BottomBand>

      <Dialog open={openDialog} handleClose={() => setOpenDialog(false)} />
    </FooterWrapper>
  );
};

export default SecondFooter;
